import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
export function FoundersQA() {
  return <div className="min-h-screen bg-brand-cream font-sans text-brand-black" data-id="element-432">
      {/* Hero Section */}
      <section className="relative w-full pt-32 pb-24 px-4 sm:px-6 lg:px-8 bg-[#1C3A2E] overflow-hidden" data-id="element-433">
        {/* Decorative SVG Background */}
        <div className="absolute inset-0 opacity-10 pointer-events-none flex items-end justify-center" data-id="element-434">
          <svg viewBox="0 0 800 400" className="w-full max-w-5xl h-auto" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-435">
            <path d="M400 400 C400 200 200 100 0 100" stroke="#FFFBF5" strokeWidth="2" data-id="element-436" />
            <path d="M400 400 C400 250 600 150 800 150" stroke="#FFFBF5" strokeWidth="2" data-id="element-437" />
            <path d="M400 400 C400 100 400 0 400 0" stroke="#FFFBF5" strokeWidth="2" data-id="element-438" />
            <circle cx="400" cy="400" r="150" stroke="#FFFBF5" strokeWidth="2" data-id="element-439" />
            <circle cx="400" cy="400" r="250" stroke="#FFFBF5" strokeWidth="2" data-id="element-440" />
          </svg>
        </div>

        <div className="max-w-4xl mx-auto relative z-10" data-id="element-441">
          <Link to="/blog" className="inline-flex items-center text-sm font-bold text-brand-orange hover:text-brand-cream transition-colors mb-8" data-id="element-442">
            <ArrowLeft className="h-4 w-4 mr-2" data-id="element-443" />
            Back to Blog
          </Link>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-444">
            <span className="inline-block px-3 py-1 mb-6 text-xs font-bold uppercase tracking-[0.2em] text-brand-orange border border-brand-orange rounded-sm" data-id="element-445">
              Founders' Q&A
            </span>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-black text-brand-cream mb-6 leading-tight" data-id="element-446">
              Why We Built ReGrowth
            </h1>
            <p className="text-xl text-brand-cream/80 max-w-2xl mb-8 leading-relaxed" data-id="element-447">
              A conversation with the founders about fat liberation, learning,
              and building something that matters.
            </p>
            <p className="text-sm font-medium text-brand-cream/60 uppercase tracking-wider" data-id="element-448">
              Published January 2025 · 15 min read
            </p>
          </motion.div>
        </div>
      </section>

      {/* Author Section */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-12 relative z-20 mb-16" data-id="element-449">
        <div className="bg-white border-2 border-brand-black p-8 shadow-xl" data-id="element-450">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-451">
            <div className="flex items-center gap-4" data-id="element-452">
              <div className="w-20 h-20 rounded-full bg-brand-magenta border-2 border-brand-black flex-shrink-0" data-id="element-453"></div>
              <div data-id="element-454">
                <h3 className="text-xl font-black text-brand-black mb-1" data-id="element-455">
                  Founder A
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed" data-id="element-456">
                  Co-founder & Creative Director. Writer, educator, and fat
                  liberation advocate.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4" data-id="element-457">
              <div className="w-20 h-20 rounded-full bg-brand-cyan border-2 border-brand-black flex-shrink-0" data-id="element-458"></div>
              <div data-id="element-459">
                <h3 className="text-xl font-black text-brand-black mb-1" data-id="element-460">
                  Founder B
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed" data-id="element-461">
                  Co-founder & Learning Designer. Researcher, facilitator, and
                  accessibility specialist.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Q&A Content */}
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-24" data-id="element-462">
        <div className="space-y-16" data-id="element-463">
          {/* Q1 */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-464">
            <div className="mb-6" data-id="element-465">
              <span className="text-sm font-black text-brand-orange mb-2 block" data-id="element-466">
                Q1
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-brand-navy leading-tight" data-id="element-467">
                How did you meet, and when did you first realise you wanted to
                build something together?
              </h2>
            </div>
            <div className="space-y-6 text-lg text-gray-700 leading-relaxed" data-id="element-468">
              <div data-id="element-469">
                <span className="font-bold text-brand-magenta uppercase text-sm tracking-wider block mb-1" data-id="element-470">
                  Founder A:
                </span>
                <p data-id="element-471">
                  We met through shared activism and education work. We were
                  both running workshops in different spaces but kept running
                  into the same limitations. I remember a specific conversation
                  after a panel where we just looked at each other and said,
                  "This isn't enough."
                </p>
              </div>
              <div data-id="element-472">
                <span className="font-bold text-brand-cyan uppercase text-sm tracking-wider block mb-1" data-id="element-473">
                  Founder B:
                </span>
                <p data-id="element-474">
                  Exactly. I recognised immediately that our skills were
                  complementary. I had the research and facilitation background,
                  and Founder A had this incredible editorial and creative
                  vision. It felt like if we combined forces, we could build the
                  platform we both wished existed.
                </p>
              </div>
            </div>
          </motion.div>

          <div className="flex items-center justify-center py-4 opacity-30" data-id="element-475">
            <div className="h-px w-16 bg-brand-black" data-id="element-476"></div>
            <div className="w-2 h-2 rounded-full bg-brand-orange mx-4" data-id="element-477"></div>
            <div className="h-px w-16 bg-brand-black" data-id="element-478"></div>
          </div>

          {/* Q2 */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-479">
            <div className="mb-6" data-id="element-480">
              <span className="text-sm font-black text-brand-orange mb-2 block" data-id="element-481">
                Q2
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-brand-navy leading-tight" data-id="element-482">
                What was missing in the learning space that made you start
                ReGrowth?
              </h2>
            </div>
            <div className="text-lg text-gray-700 leading-relaxed" data-id="element-483">
              <p data-id="element-484">
                There's a massive gap between academic fat studies and
                accessible, everyday education. You either have dense, paywalled
                academic papers, or you have watered-down "body positivity" that
                ignores systemic oppression. We wanted to build a
                bridge—rigorous, justice-centred learning that is actually
                accessible to the people who need it most, whether they are
                individuals unlearning bias or workplaces trying to do better.
              </p>
            </div>
          </motion.div>

          <div className="flex items-center justify-center py-4 opacity-30" data-id="element-485">
            <div className="h-px w-16 bg-brand-black" data-id="element-486"></div>
            <div className="w-2 h-2 rounded-full bg-brand-orange mx-4" data-id="element-487"></div>
            <div className="h-px w-16 bg-brand-black" data-id="element-488"></div>
          </div>

          {/* Q3 */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-489">
            <div className="mb-6" data-id="element-490">
              <span className="text-sm font-black text-brand-orange mb-2 block" data-id="element-491">
                Q3
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-brand-navy leading-tight" data-id="element-492">
                Why the name "ReGrowth"? What does it mean to you?
              </h2>
            </div>
            <div className="space-y-6 text-lg text-gray-700 leading-relaxed" data-id="element-493">
              <div data-id="element-494">
                <span className="font-bold text-brand-magenta uppercase text-sm tracking-wider block mb-1" data-id="element-495">
                  Founder A:
                </span>
                <p data-id="element-496">
                  For me, it's about regeneration. When a forest burns, the
                  growth that comes after is often stronger and more resilient.
                  Fat people have had so much burned away by systemic bias.
                  ReGrowth is about what we build in the aftermath.
                </p>
              </div>
              <div data-id="element-497">
                <span className="font-bold text-brand-cyan uppercase text-sm tracking-wider block mb-1" data-id="element-498">
                  Founder B:
                </span>
                <p data-id="element-499">
                  It also speaks to the process of unlearning. You can't just
                  erase bias; you have to actively regrow new neural pathways,
                  new understandings, and new ways of relating to bodies. It's
                  an active, ongoing process of cultivation.
                </p>
              </div>
            </div>
          </motion.div>

          <div className="flex items-center justify-center py-4 opacity-30" data-id="element-500">
            <div className="h-px w-16 bg-brand-black" data-id="element-501"></div>
            <div className="w-2 h-2 rounded-full bg-brand-orange mx-4" data-id="element-502"></div>
            <div className="h-px w-16 bg-brand-black" data-id="element-503"></div>
          </div>

          {/* Q4 */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-504">
            <div className="mb-6" data-id="element-505">
              <span className="text-sm font-black text-brand-orange mb-2 block" data-id="element-506">
                Q4
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-brand-navy leading-tight" data-id="element-507">
                What is Waestm, and how does it connect to ReGrowth?
              </h2>
            </div>
            <div className="text-lg text-gray-700 leading-relaxed" data-id="element-508">
              <p data-id="element-509">
                WAESTM is our publication arm. While ReGrowth focuses on
                structured learning experiences and courses, WAESTM is the space
                for cultural critique, essays, poetry, and art. They feed into
                each other. The ideas explored in WAESTM often become the
                foundation for our courses, and the questions raised in our
                courses find their way into WAESTM's editorial focus.
              </p>
            </div>
          </motion.div>

          <div className="flex items-center justify-center py-4 opacity-30" data-id="element-510">
            <div className="h-px w-16 bg-brand-black" data-id="element-511"></div>
            <div className="w-2 h-2 rounded-full bg-brand-orange mx-4" data-id="element-512"></div>
            <div className="h-px w-16 bg-brand-black" data-id="element-513"></div>
          </div>

          {/* Q5 */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-514">
            <div className="mb-6" data-id="element-515">
              <span className="text-sm font-black text-brand-orange mb-2 block" data-id="element-516">
                Q5
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-brand-navy leading-tight" data-id="element-517">
                Why begin with fat liberation?
              </h2>
            </div>
            <div className="text-lg text-gray-700 leading-relaxed mb-8" data-id="element-518">
              <p data-id="element-519">
                Fat oppression is one of the most undertaught and misunderstood
                forms of systemic violence. It's still socially acceptable in
                spaces that consider themselves progressive. We start here
                because it's our lived experience, but also because untangling
                anti-fatness requires untangling racism, ableism, and classism.
                It's a vital thread to pull if we want to dismantle oppressive
                systems.
              </p>
            </div>

            <div className="border-l-4 border-brand-orange bg-brand-orange/5 p-8 my-8" data-id="element-520">
              <p className="text-2xl font-bold text-brand-navy italic leading-snug" data-id="element-521">
                "Fat liberation isn't a niche issue — it's connected to every
                system of oppression we're taught to accept as normal."
              </p>
            </div>
          </motion.div>

          <div className="flex items-center justify-center py-4 opacity-30" data-id="element-522">
            <div className="h-px w-16 bg-brand-black" data-id="element-523"></div>
            <div className="w-2 h-2 rounded-full bg-brand-orange mx-4" data-id="element-524"></div>
            <div className="h-px w-16 bg-brand-black" data-id="element-525"></div>
          </div>

          {/* Q6 */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-526">
            <div className="mb-6" data-id="element-527">
              <span className="text-sm font-black text-brand-orange mb-2 block" data-id="element-528">
                Q6
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-brand-navy leading-tight" data-id="element-529">
                How do your skills and perspectives complement each other?
              </h2>
            </div>
            <div className="space-y-6 text-lg text-gray-700 leading-relaxed" data-id="element-530">
              <div data-id="element-531">
                <span className="font-bold text-brand-magenta uppercase text-sm tracking-wider block mb-1" data-id="element-532">
                  Founder A:
                </span>
                <p data-id="element-533">
                  I bring the editorial eye and the creative direction. I'm
                  always thinking about how a concept feels, how the story is
                  told, and how we can make complex theory resonate emotionally.
                </p>
              </div>
              <div data-id="element-534">
                <span className="font-bold text-brand-cyan uppercase text-sm tracking-wider block mb-1" data-id="element-535">
                  Founder B:
                </span>
                <p data-id="element-536">
                  And I ground that in pedagogical structure. I focus on how
                  people actually learn, how to design for accessibility, and
                  ensuring our frameworks are backed by robust research. We push
                  each other to be both rigorous and engaging.
                </p>
              </div>
            </div>
          </motion.div>

          <div className="flex items-center justify-center py-4 opacity-30" data-id="element-537">
            <div className="h-px w-16 bg-brand-black" data-id="element-538"></div>
            <div className="w-2 h-2 rounded-full bg-brand-orange mx-4" data-id="element-539"></div>
            <div className="h-px w-16 bg-brand-black" data-id="element-540"></div>
          </div>

          {/* Q7 */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-541">
            <div className="mb-6" data-id="element-542">
              <span className="text-sm font-black text-brand-orange mb-2 block" data-id="element-543">
                Q7
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-brand-navy leading-tight" data-id="element-544">
                What does ethical or justice-centred learning look like to you?
              </h2>
            </div>
            <div className="text-lg text-gray-700 leading-relaxed" data-id="element-545">
              <p data-id="element-546">
                It looks like co-production. It means centering the voices of
                those with lived experience, not just as subjects of study, but
                as the experts and architects of the learning itself. It means
                designing for accessibility from the start, not as an
                afterthought. And it means moving away from compliance-driven
                "diversity training" toward genuine, transformative education
                that challenges power dynamics.
              </p>
            </div>
          </motion.div>

          <div className="flex items-center justify-center py-4 opacity-30" data-id="element-547">
            <div className="h-px w-16 bg-brand-black" data-id="element-548"></div>
            <div className="w-2 h-2 rounded-full bg-brand-orange mx-4" data-id="element-549"></div>
            <div className="h-px w-16 bg-brand-black" data-id="element-550"></div>
          </div>

          {/* Q8 */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-551">
            <div className="mb-6" data-id="element-552">
              <span className="text-sm font-black text-brand-orange mb-2 block" data-id="element-553">
                Q8
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-brand-navy leading-tight" data-id="element-554">
                What's something you've had to unlearn in your own growth?
              </h2>
            </div>
            <div className="space-y-6 text-lg text-gray-700 leading-relaxed mb-8" data-id="element-555">
              <div data-id="element-556">
                <span className="font-bold text-brand-magenta uppercase text-sm tracking-wider block mb-1" data-id="element-557">
                  Founder A:
                </span>
                <p data-id="element-558">
                  I had to unlearn the idea that my worth was tied to my
                  productivity or my proximity to thinness. The "good fatty"
                  trope is deeply ingrained, and letting go of the need to
                  perform health or respectability was a long process.
                </p>
              </div>
              <div data-id="element-559">
                <span className="font-bold text-brand-cyan uppercase text-sm tracking-wider block mb-1" data-id="element-560">
                  Founder B:
                </span>
                <p data-id="element-561">
                  For me, it was unlearning the academic impulse to
                  intellectualise everything as a defense mechanism. Sometimes
                  the work isn't about citing the right theorist; it's about
                  sitting with the discomfort of lived reality.
                </p>
              </div>
            </div>

            <div className="border-l-4 border-brand-orange bg-brand-orange/5 p-8 my-8" data-id="element-562">
              <p className="text-2xl font-bold text-brand-navy italic leading-snug" data-id="element-563">
                "Unlearning isn't a destination. It's a practice — messy,
                ongoing, and deeply humbling."
              </p>
            </div>
          </motion.div>

          <div className="flex items-center justify-center py-4 opacity-30" data-id="element-564">
            <div className="h-px w-16 bg-brand-black" data-id="element-565"></div>
            <div className="w-2 h-2 rounded-full bg-brand-orange mx-4" data-id="element-566"></div>
            <div className="h-px w-16 bg-brand-black" data-id="element-567"></div>
          </div>

          {/* Q9 */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-568">
            <div className="mb-6" data-id="element-569">
              <span className="text-sm font-black text-brand-orange mb-2 block" data-id="element-570">
                Q9
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-brand-navy leading-tight" data-id="element-571">
                Who is ReGrowth really for?
              </h2>
            </div>
            <div className="text-lg text-gray-700 leading-relaxed" data-id="element-572">
              <p data-id="element-573">
                It's for anyone ready to do the work. We have pathways for
                individuals beginning their unlearning journey, for educators
                wanting to create affirming classrooms, and for workplaces ready
                to address structural bias. But at its core, ReGrowth is for
                people who are tired of superficial inclusion and want to engage
                with the root causes of systemic harm.
              </p>
            </div>
          </motion.div>

          <div className="flex items-center justify-center py-4 opacity-30" data-id="element-574">
            <div className="h-px w-16 bg-brand-black" data-id="element-575"></div>
            <div className="w-2 h-2 rounded-full bg-brand-orange mx-4" data-id="element-576"></div>
            <div className="h-px w-16 bg-brand-black" data-id="element-577"></div>
          </div>

          {/* Q10 */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-578">
            <div className="mb-6" data-id="element-579">
              <span className="text-sm font-black text-brand-orange mb-2 block" data-id="element-580">
                Q10
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-brand-navy leading-tight" data-id="element-581">
                What kind of change do you hope people experience after engaging
                with your work?
              </h2>
            </div>
            <div className="text-lg text-gray-700 leading-relaxed" data-id="element-582">
              <p data-id="element-583">
                We hope people experience a shift from shame to understanding,
                and from compliance to justice. We want individuals to feel
                equipped to advocate for themselves and others. We want
                organisations to stop seeing anti-fatness as a personal failing
                and start addressing it as a structural issue. Ultimately, we
                want to build a community that feels empowered to imagine and
                create a more liberated world.
              </p>
            </div>
          </motion.div>
        </div>
      </article>

      {/* Closing Section */}
      <section className="bg-white border-y-2 border-brand-black py-20 px-4 sm:px-6 lg:px-8 text-center" data-id="element-584">
        <div className="max-w-3xl mx-auto" data-id="element-585">
          <p className="text-2xl font-medium text-brand-black mb-10 leading-relaxed" data-id="element-586">
            The work of liberation is collective. We're just getting started,
            and we invite you to grow with us.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-587">
            <Link to="/" className="inline-flex items-center justify-center px-8 py-4 bg-brand-orange text-white font-bold uppercase tracking-wide hover:bg-brand-magenta transition-colors" data-id="element-588">
              Explore Our Work
            </Link>
            <Link to="/clusters/read-waestm" className="inline-flex items-center justify-center px-8 py-4 border-2 border-brand-black text-brand-black font-bold uppercase tracking-wide hover:bg-brand-black hover:text-white transition-colors" data-id="element-589">
              Read WAESTM
            </Link>
          </div>
        </div>
      </section>

      {/* Newsletter Signup */}
      <section className="py-20 bg-brand-navy text-white" data-id="element-590">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-591">
          <h2 className="text-3xl font-black uppercase mb-6" data-id="element-592">Stay Connected</h2>
          <p className="text-xl mb-8 opacity-90" data-id="element-593">
            Join our newsletter for updates on new courses, essays, and
            resources.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto" data-id="element-594">
            <input type="email" placeholder="Your email address" className="flex-1 px-4 py-3 text-brand-black border-2 border-white focus:outline-none" data-id="element-595" />
            <button className="px-8 py-3 bg-white text-brand-navy font-bold uppercase tracking-wide hover:bg-gray-100 transition-colors" data-id="element-596">
              Subscribe
            </button>
          </div>
        </div>
      </section>
    </div>;
}